import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import axios from "axios";
import { config } from "../App";
import Cart, {
  generateCartItemsFrom,
  getTotalCartValue,
} from "./Cart";
import Header from "./Header";
import Footer from "./Footer";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import "./Checkout.css";

const Checkout = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [showAddAddress, setShowAddAddress] = useState(false);

  const token = localStorage.getItem("token");

  // ===============================
  // Redirect if not logged in
  // ===============================
  useEffect(() => {
    if (!token) {
      enqueueSnackbar(
        "You must be logged in to access checkout page",
        { variant: "info" }
      );
      history.push("/login");
      return;
    }

    initialize();
  }, []);

  // ===============================
  // Initial data fetch
  // ===============================
  const initialize = async () => {
    try {
      const productRes = await axios.get(`${config.endpoint}/products`);
      setProducts(productRes.data);

      const cartRes = await axios.get(`${config.endpoint}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cart = generateCartItemsFrom(
        cartRes.data,
        productRes.data
      );
      setCartItems(cart);

      getAddresses();
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // Fetch addresses
  // ===============================
  const getAddresses = async () => {
    try {
      const res = await axios.get(
        `${config.endpoint}/user/addresses`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setAddresses(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // Add Address
  // ===============================
  const addAddress = async () => {
    if (!newAddress.trim()) return;

    try {
      const res = await axios.post(
        `${config.endpoint}/user/addresses`,
        { address: newAddress },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // ✅ Use response data directly
      setAddresses(res.data);
      setNewAddress("");
      setShowAddAddress(false);
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // Delete Address
  // ===============================
  const deleteAddress = async (id) => {
    try {
      const res = await axios.delete(
        `${config.endpoint}/user/addresses/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // ✅ Use response data directly
      setAddresses(res.data);

      if (selectedAddress === id) {
        setSelectedAddress("");
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // Place Order
  // ===============================
  const placeOrder = async () => {
    if (!selectedAddress) {
      enqueueSnackbar("Please select one shipping address", {
        variant: "warning",
      });
      return;
    }

    const balance = parseInt(localStorage.getItem("balance"));
    const total = getTotalCartValue(cartItems);

    if (balance < total) {
      enqueueSnackbar("You do not have enough balance", {
        variant: "warning",
      });
      return;
    }

    try {
      await axios.post(
        `${config.endpoint}/cart/checkout`,
        { addressId: selectedAddress },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      history.push("/thanks");
    } catch (err) {
      console.log(err);
    }
  };

  // ===============================
  // UI
  // ===============================
  return (
    <div>
      <Header />
      <Grid container spacing={2} padding="2rem">
        <Grid item xs={12} md={8}>
          {/* Shipping */}
          <Typography variant="h5" gutterBottom>
            Shipping
          </Typography>

          {addresses.length === 0 && (
            <Typography color="textSecondary">
              No addresses found for this account. Please add one to
              proceed
            </Typography>
          )}

          {addresses.map((addr) => (
            <Box
              key={addr._id}
              className={`address-item ${
                selectedAddress === addr._id
                  ? "selected"
                  : "not-selected"
              }`}
              onClick={() => setSelectedAddress(addr._id)}
            >
              <Box>{addr.address}</Box>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteAddress(addr._id);
                }}
              >
                DELETE
              </Button>
            </Box>
          ))}

          {/* Add Address Section */}
          {!showAddAddress ? (
            <Button
              variant="contained"
              onClick={() => setShowAddAddress(true)}
            >
              Add new address
            </Button>
          ) : (
            <Box marginTop="1rem">
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your complete address"
                value={newAddress}
                onChange={(e) =>
                  setNewAddress(e.target.value)
                }
              />
              <Box marginTop="1rem">
                <Button
                  variant="contained"
                  onClick={addAddress}
                >
                  ADD
                </Button>
                <Button
                  onClick={() => setShowAddAddress(false)}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          )}

          {/* Payment */}
          <Box marginTop="2rem">
            <Typography variant="h5">Payment</Typography>
            <Typography>Wallet</Typography>
            <Typography>
              Pay ${getTotalCartValue(cartItems)} of available $
              {localStorage.getItem("balance")}
            </Typography>

            <Button
              variant="contained"
              onClick={placeOrder}
              style={{ marginTop: "1rem" }}
            >
              PLACE ORDER
            </Button>
          </Box>
        </Grid>

        {/* Cart readonly */}
        <Grid item xs={12} md={4}>
          <Cart
            products={products}
            items={cartItems}
            readonly
          />
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

export default Checkout;
